import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { Line } from 'react-chartjs-2';

import { init, getResumo, getMedicoes, limparMedicoes, getBaseline, limparBaseline } from './transitoActions';
import { getGrupoFisicoList, getConfControladorGrupoList } from '../common/selectdb/selectdbAtions';
import SPButton from '../common/form/spButton';
import ReactTableGT2A from '../common/grid/gridReactTable';
import PremiumTableShell from '../common/grid/premiumTableShell';
import { premiumBadge } from '../manutencao/manutencaoListUtils';

const REFRESH_MS = 60 * 1000;

const PERIODOS = [
  { value: '6', label: '6 horas' },
  { value: '12', label: '12 horas' },
  { value: '24', label: '24 horas' },
  { value: '48', label: '48 horas' },
  { value: '168', label: '7 dias' },
];

const OPCOES_BASE = {
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    display: true,
    position: 'bottom',
    labels: { boxWidth: 12, padding: 14, fontColor: '#64748b', fontSize: 11, usePointStyle: true },
  },
  tooltips: {
    backgroundColor: 'rgba(15, 23, 42, 0.92)',
    titleFontStyle: '600',
    bodyFontSize: 12,
    cornerRadius: 8,
    xPadding: 12,
    yPadding: 10,
    mode: 'index',
    intersect: false,
  },
  elements: { line: { tension: 0.3 } },
};

const OPCOES_TEMPO = {
  ...OPCOES_BASE,
  scales: {
    yAxes: [
      {
        ticks: { beginAtZero: true, fontColor: '#64748b', fontSize: 11, callback: v => `${v}s` },
        gridLines: { color: 'rgba(148, 163, 184, 0.15)', drawBorder: false },
        scaleLabel: { display: true, labelString: 'Tempo de travessia (s)', fontColor: '#94a3b8', fontSize: 11 },
      },
    ],
    xAxes: [{ ticks: { fontColor: '#64748b', fontSize: 10, maxTicksLimit: 12 }, gridLines: { display: false } }],
  },
};

const OPCOES_RATIO = {
  ...OPCOES_BASE,
  scales: {
    yAxes: [
      {
        ticks: { beginAtZero: false, fontColor: '#64748b', fontSize: 11, callback: v => `${v}x` },
        gridLines: { color: 'rgba(148, 163, 184, 0.15)', drawBorder: false },
        scaleLabel: { display: true, labelString: 'Índice (real ÷ esperado)', fontColor: '#94a3b8', fontSize: 11 },
      },
    ],
    xAxes: [{ ticks: { fontColor: '#64748b', fontSize: 10, maxTicksLimit: 12 }, gridLines: { display: false } }],
  },
};

const OPCOES_VELOCIDADE = {
  ...OPCOES_BASE,
  scales: {
    yAxes: [
      {
        ticks: { beginAtZero: true, fontColor: '#64748b', fontSize: 11, callback: v => `${v} km/h` },
        gridLines: { color: 'rgba(148, 163, 184, 0.15)', drawBorder: false },
        scaleLabel: { display: true, labelString: 'Velocidade (km/h)', fontColor: '#94a3b8', fontSize: 11 },
      },
    ],
    xAxes: [{ ticks: { fontColor: '#64748b', fontSize: 10, maxTicksLimit: 12 }, gridLines: { display: false } }],
  },
};

function formatLabel(medidoEm, horas) {
  const m = moment(medidoEm);
  return horas > 24 ? m.format('DD/MM HH:mm') : m.format('HH:mm');
}

function formatDuracao(segundos) {
  if ((segundos === null || segundos === undefined)) return '—';
  const n = Number(segundos);
  if (!Number.isFinite(n)) return '—';
  if (n < 60) {
    const arred = Math.round(n * 10) / 10;
    return arred % 1 === 0 ? `${arred}s` : `${arred.toFixed(1)}s`;
  }
  const min = Math.floor(n / 60);
  const seg = Math.round(n % 60);
  return seg ? `${min}min ${seg}s` : `${min}min`;
}

function enriquecerMedicao(medicao, fase) {
  if (!medicao && !fase) return null;
  const base = medicao || {};
  const distancia =
    (base.distancia_m !== null && base.distancia_m !== undefined)
      ? Number(base.distancia_m)
      : fase && (fase.distancia_trecho_m !== null && fase.distancia_trecho_m !== undefined)
        ? Number(fase.distancia_trecho_m)
        : null;
  const duracao_real_s = (base.duracao_real_s !== null && base.duracao_real_s !== undefined) ? Number(base.duracao_real_s) : null;
  const velocidade_real = (base.velocidade_kmh !== null && base.velocidade_kmh !== undefined) ? Number(base.velocidade_kmh) : null;
  const duracao_esperada_s = (base.duracao_esperada_s !== null && base.duracao_esperada_s !== undefined) ? Number(base.duracao_esperada_s) : null;
  const ratio = (base.ratio !== null && base.ratio !== undefined) ? Number(base.ratio) : null;
  const origem = base.esperado_origem || null;

  const velocidade_esperada_kmh = distancia && duracao_esperada_s ? (distancia / duracao_esperada_s) * 3.6 : null;
  const limiar = (base.ratio_limiar !== null && base.ratio_limiar !== undefined) ? Number(base.ratio_limiar) : fase && fase.ratio_limiar;
  const calibrando = origem === 'google';

  return {
    ...base,
    distancia_m: distancia,
    duracao_esperada_s,
    duracao_real_s,
    ratio,
    velocidade_esperada_kmh,
    velocidade_kmh: velocidade_real,
    esperado_origem: origem,
    calibrando,
    congestionado: !calibrando && (ratio !== null && ratio !== undefined) && (limiar !== null && limiar !== undefined) ? ratio >= limiar : base.congestionado,
  };
}

function rotuloFaseOpcao(fase) {
  const anel = (fase.anel !== null && fase.anel !== undefined) ? `Anel ${fase.anel}` : 'Anel —';
  const titulo = fase.rotulo && fase.rotulo !== fase.cruzamento ? ` — ${fase.rotulo}` : '';
  return `${anel} · Fase ${fase.fase_id}${titulo}`;
}

function formatarControlador(item) {
  if (!item || (item.numero_controlador === null || item.numero_controlador === undefined)) return '—';
  const numero = String(item.numero_controlador).padStart(2, '0');
  const endereco = item.cruzamento || item.endereco || item.controlador_endereco;
  return endereco ? `Ctrl ${numero} — ${endereco}` : `Ctrl ${numero}`;
}

function formatVelocidade(kmh) {
  if ((kmh === null || kmh === undefined) || !Number.isFinite(kmh)) return '—';
  return kmh < 10 ? `${kmh.toFixed(1)} km/h` : `${Math.round(kmh)} km/h`;
}

function labelOrigemEsperado(origem, semDados) {
  if (semDados) return '';
  if (origem === 'historico') return ' (histórico)';
  if (origem === 'manual') return ' (manual)';
  if (origem === 'google') return ' (calibrando)';
  return '';
}

class TransitoMonitor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      grupoFisicoId: '',
      numeroControlador: '',
      anel: '',
      chaveFase: '',
      periodo: '24',
    };
    this.handleSelectFase = this.handleSelectFase.bind(this);
    this.atualizar = this.atualizar.bind(this);
    this.getTrProps = this.getTrProps.bind(this);
  }

  componentDidMount() {
    this.props.getGrupoFisicoList();
    this.props.getConfControladorGrupoList();
    this.timer = setInterval(() => this.atualizar(), REFRESH_MS);
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
  }

  atualizar() {
    this.props.getResumo();
    const fase = this.faseSelecionada();
    if (fase) {
      this.props.getMedicoes(fase.controlador_id, fase.fase_id, Number(this.state.periodo));
    }
  }

  faseSelecionada() {
    const { chaveFase } = this.state;
    if (!chaveFase) return null;
    const fases = this.fasesDisponiveis();
    return fases.find(f => `${f.controlador_id}-${f.fase_id}` === chaveFase) || null;
  }

  fasesDisponiveis() {
    const resumo = this.props.resumo || [];
    if (resumo.length) return resumo;
    return this.props.fases || [];
  }

  fasesBaseFiltradas() {
    const { grupoFisicoId, numeroControlador, anel } = this.state;
    return this.fasesDisponiveis().filter(f => {
      if (grupoFisicoId && String(f.grupo_fisico_id) !== String(grupoFisicoId)) return false;
      if (numeroControlador && String(f.numero_controlador) !== String(numeroControlador)) return false;
      if (anel && String(f.anel) !== String(anel)) return false;
      return true;
    });
  }

  carregarMedicoes(chaveFase, periodo) {
    const fase = this.fasesDisponiveis().find(f => `${f.controlador_id}-${f.fase_id}` === chaveFase);
    if (!fase) {
      this.props.limparMedicoes();
      this.props.limparBaseline();
      return;
    }
    this.props.getMedicoes(fase.controlador_id, fase.fase_id, Number(periodo));
    this.props.getBaseline(fase.controlador_id, fase.fase_id);
  }

  handleSelectFase(chaveFase) {
    this.setState({ chaveFase });
    this.carregarMedicoes(chaveFase, this.state.periodo);
  }

  handleChangePeriodo(periodo) {
    this.setState({ periodo });
    if (this.state.chaveFase) this.carregarMedicoes(this.state.chaveFase, periodo);
  }

  handleGrupoChange(grupoFisicoId) {
    this.setState({ grupoFisicoId, numeroControlador: '', anel: '', chaveFase: '' });
    this.props.getConfControladorGrupoList(grupoFisicoId || undefined);
    this.props.limparMedicoes();
    this.props.limparBaseline();
  }

  handleControladorChange(numeroControlador) {
    this.setState({ numeroControlador, anel: '', chaveFase: '' });
    this.props.limparMedicoes();
    this.props.limparBaseline();
  }

  handleAnelChange(anel) {
    this.setState({ anel, chaveFase: '' });
    this.props.limparMedicoes();
    this.props.limparBaseline();
  }

  gruposFiltro() {
    const idsMonitorados = new Set(
      this.fasesDisponiveis()
        .map(f => String(f.grupo_fisico_id))
        .filter(Boolean),
    );
    return (this.props.listGrupoFisico || []).filter(g => idsMonitorados.has(String(g.id)));
  }

  controladoresFiltro() {
    const { grupoFisicoId } = this.state;
    const mapa = new Map();
    this.fasesDisponiveis()
      .filter(f => !grupoFisicoId || String(f.grupo_fisico_id) === String(grupoFisicoId))
      .forEach(f => {
        if ((f.numero_controlador === null || f.numero_controlador === undefined)) return;
        const key = String(f.numero_controlador);
        if (!mapa.has(key)) {
          mapa.set(key, {
            numero: f.numero_controlador,
            endereco: f.endereco || f.cruzamento,
          });
        }
      });
    return Array.from(mapa.values()).sort((a, b) => Number(a.numero) - Number(b.numero));
  }

  aneisDisponiveis() {
    const { numeroControlador } = this.state;
    const set = new Set();
    this.fasesDisponiveis()
      .filter(f => !numeroControlador || String(f.numero_controlador) === String(numeroControlador))
      .forEach(f => {
        if ((f.anel !== null && f.anel !== undefined)) set.add(Number(f.anel));
      });
    return Array.from(set).sort((a, b) => a - b);
  }

  fasesFiltradas() {
    return this.fasesBaseFiltradas();
  }

  getTrProps(state, rowInfo) {
    if (!rowInfo || !rowInfo.original) return {};
    const chave = `${rowInfo.original.controlador_id}-${rowInfo.original.fase_id}`;
    const selecionado = chave === this.state.chaveFase;
    return {
      onClick: () => this.handleSelectFase(chave),
      style: { cursor: 'pointer' },
      className: selecionado ? 'transito-row--selected' : '',
    };
  }

  dadosGraficos() {
    const fase = this.faseSelecionada();
    const { medicoes } = this.props;
    const serie = ((medicoes && medicoes.medicoes) || []).map(m => enriquecerMedicao(m, fase));
    const horas = (medicoes && medicoes.horas) || 24;
    const labels = serie.map(m => formatLabel(m.medido_em, horas));
    const limiar = serie.length ? serie[serie.length - 1].ratio_limiar : null;

    const tempo = {
      labels,
      datasets: [
        {
          label: 'Esperado (sem trânsito)',
          data: serie.map(m => m.duracao_esperada_s),
          borderColor: '#27ae60',
          backgroundColor: 'rgba(39, 174, 96, 0.06)',
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 2,
          fill: false,
        },
        {
          label: 'Real (com trânsito)',
          data: serie.map(m => m.duracao_real_s),
          borderColor: '#e74c3c',
          backgroundColor: 'rgba(231, 76, 60, 0.08)',
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 2,
          fill: true,
        },
      ],
    };

    const ratioDatasets = [
      {
        label: 'Índice de congestionamento',
        data: serie.map(m => m.ratio),
        borderColor: '#3c8dbc',
        backgroundColor: 'rgba(60, 141, 188, 0.08)',
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 2,
        fill: true,
      },
    ];
    if (limiar) {
      ratioDatasets.push({
        label: `Limiar de alerta (${Number(limiar).toFixed(2)}x)`,
        data: serie.map(() => limiar),
        borderColor: '#f39c12',
        borderDash: [6, 4],
        pointRadius: 0,
        borderWidth: 1.5,
        fill: false,
      });
    }

    const velocidade = {
      labels,
      datasets: [
        {
          label: 'Esperada (sem trânsito)',
          data: serie.map(m => m.velocidade_esperada_kmh),
          borderColor: '#27ae60',
          backgroundColor: 'rgba(39, 174, 96, 0.06)',
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 2,
          fill: false,
        },
        {
          label: 'Real (com trânsito)',
          data: serie.map(m => m.velocidade_kmh),
          borderColor: '#e74c3c',
          backgroundColor: 'rgba(231, 76, 60, 0.08)',
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 2,
          fill: true,
        },
      ],
    };

    return { tempo, ratio: { labels, datasets: ratioDatasets }, velocidade, temDados: serie.length > 0 };
  }

  renderFiltros(semFases = false) {
    const grupos = this.gruposFiltro();
    const controladores = this.controladoresFiltro();
    const aneis = this.aneisDisponiveis();
    const fasesFiltradas = this.fasesFiltradas();

    return (
      <div className={`os-list-filters-bar${semFases ? ' is-disabled' : ''}`}>
        <div className="os-list-filter-group">
          <label className="os-list-filter-label" htmlFor="transito-grupo">
            Grupo Físico
          </label>
          <select
            id="transito-grupo"
            className="form-control os-list-filter-select"
            value={this.state.grupoFisicoId}
            onChange={e => this.handleGrupoChange(e.target.value)}
            disabled={semFases}
          >
            <option value="">Todos</option>
            {grupos.map(g => (
              <option key={g.id} value={String(g.id)}>
                {g.descricao}
              </option>
            ))}
          </select>
        </div>
        <div className="os-list-filter-group">
          <label className="os-list-filter-label" htmlFor="transito-controlador">
            Controlador
          </label>
          <select
            id="transito-controlador"
            className="form-control os-list-filter-select os-list-filter-select-ctrl"
            value={this.state.numeroControlador}
            onChange={e => this.handleControladorChange(e.target.value)}
            disabled={semFases}
          >
            <option value="">Todos</option>
            {controladores.map(c => (
              <option key={c.numero} value={String(c.numero)}>
                {`Ctrl ${String(c.numero).padStart(2, '0')}${c.endereco ? ` — ${c.endereco}` : ''}`}
              </option>
            ))}
          </select>
        </div>
        <div className="os-list-filter-group">
          <label className="os-list-filter-label" htmlFor="transito-anel">
            Anel
          </label>
          <select
            id="transito-anel"
            className="form-control os-list-filter-select"
            value={this.state.anel}
            onChange={e => this.handleAnelChange(e.target.value)}
            disabled={semFases || !this.state.numeroControlador}
          >
            <option value="">Todos</option>
            {aneis.map(a => (
              <option key={a} value={a}>
                Anel {a}
              </option>
            ))}
          </select>
        </div>
        <div className="os-list-filter-group">
          <label className="os-list-filter-label" htmlFor="transito-fase">
            Fase
          </label>
          <select
            id="transito-fase"
            className="form-control os-list-filter-select"
            value={this.state.chaveFase}
            onChange={e => this.handleSelectFase(e.target.value)}
            disabled={semFases}
          >
            <option value="">Selecione na tabela…</option>
            {fasesFiltradas.map(f => (
              <option key={`${f.controlador_id}-${f.fase_id}`} value={`${f.controlador_id}-${f.fase_id}`}>
                {rotuloFaseOpcao(f)}
              </option>
            ))}
          </select>
        </div>
        <div className="os-list-filter-group">
          <label className="os-list-filter-label" htmlFor="transito-periodo">
            Período
          </label>
          <select
            id="transito-periodo"
            className="form-control os-list-filter-select"
            value={this.state.periodo}
            onChange={e => this.handleChangePeriodo(e.target.value)}
            disabled={semFases || !this.state.chaveFase}
          >
            {PERIODOS.map(p => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div className="os-list-filter-group transito-filter-actions">
          <label className="os-list-filter-label">&nbsp;</label>
          <SPButton
            type="button"
            className="default"
            icon="sync-alt"
            data-title="Atualizar agora"
            onClick={this.atualizar}
            disabled={semFases}
          />
        </div>
      </div>
    );
  }

  renderKpisDetalhe() {
    const fase = this.faseSelecionada();
    const serie = (this.props.medicoes && this.props.medicoes.medicoes) || [];
    const ultima = serie.length ? serie[serie.length - 1] : null;
    const resumoFase = fase
      ? (this.props.resumo || []).find(r => r.controlador_id === fase.controlador_id && r.fase_id === fase.fase_id)
      : null;
    const pontoBruto = ultima || resumoFase;
    const ponto = enriquecerMedicao(pontoBruto, fase);
    const MAP_ORIGEM = { historico: ' · histórico', manual: ' · manual', google: ' · calibrando' };
    const origemSufixo = ponto && ponto.esperado_origem ? MAP_ORIGEM[ponto.esperado_origem] || '' : '';

    const ratio = ponto && (ponto.ratio !== null && ponto.ratio !== undefined) ? Number(ponto.ratio) : null;
    const limiar =
      ponto && (ponto.ratio_limiar !== null && ponto.ratio_limiar !== undefined) ? Number(ponto.ratio_limiar) : (fase && fase.ratio_limiar) || null;
    const calibrando = ponto && ponto.calibrando;
    const congestionado = !calibrando && (ratio !== null && ratio !== undefined) && (limiar !== null && limiar !== undefined) && ratio >= limiar;
    const statusCor = (ratio === null || ratio === undefined) ? '#94a3b8' : calibrando ? '#3c8dbc' : congestionado ? '#e74c3c' : '#27ae60';
    const statusTxt = (ratio === null || ratio === undefined) ? 'Sem dados' : calibrando ? 'Calibrando' : congestionado ? 'Congestionado' : 'Fluido';
    const atraso =
      ponto && (ponto.duracao_real_s !== null && ponto.duracao_real_s !== undefined) && (ponto.duracao_esperada_s !== null && ponto.duracao_esperada_s !== undefined)
        ? ponto.duracao_real_s - ponto.duracao_esperada_s
        : null;

    return (
      <div className="os-graficos-kpi-grid">
        <KpiCard
          label="Status da fase"
          valor={statusTxt}
          detalhe={(ratio !== null && ratio !== undefined) ? `${ratio.toFixed(2)}x o normal` : 'Aguardando coleta'}
          cor={statusCor}
          icone={congestionado ? 'exclamation-triangle' : 'road'}
        />
        <KpiCard
          label="Velocidade"
          valor={ponto ? formatVelocidade(ponto.velocidade_kmh) : '—'}
          detalhe={
            ponto
              ? `esperado ${formatVelocidade(ponto.velocidade_esperada_kmh)}${origemSufixo}`
              : ''
          }
          cor="#3c8dbc"
          icone="tachometer-alt"
        />
        <KpiCard
          label="Tempo de travessia"
          valor={ponto ? formatDuracao(ponto.duracao_real_s) : '—'}
          detalhe={
            ponto
              ? `esperado ${formatDuracao(ponto.duracao_esperada_s)}${origemSufixo}`
              : ''
          }
          cor="#16a085"
          icone="clock"
        />
        <KpiCard
          label="Atraso"
          valor={(atraso !== null && atraso !== undefined) ? `+${formatDuracao(Math.max(atraso, 0))}` : '—'}
          detalhe={ponto && ponto.medido_em ? `atualizado ${moment(ponto.medido_em).format('HH:mm')}` : ''}
          cor="#8e44ad"
          icone="hourglass-half"
        />
      </div>
    );
  }

  renderTabela(lista) {
    const dados = lista.map(r => enriquecerMedicao(r, r));

    const columns = [
      {
        Header: 'Controlador',
        id: 'controlador',
        minWidth: 160,
        className: 'column_grid column_left',
        Cell: row => (
          <span className="transito-cell-text transito-cell-text--left">{formatarControlador(row.original)}</span>
        ),
      },
      {
        Header: 'Anel',
        accessor: 'anel',
        width: 56,
        className: 'column_grid column_center',
        Cell: row => <span className="transito-cell-text">{(row.value !== null && row.value !== undefined) ? row.value : '—'}</span>,
      },
      {
        Header: 'Fase',
        accessor: 'fase_id',
        width: 56,
        className: 'column_grid column_center',
        Cell: row => <span className="transito-cell-text">{(row.value !== null && row.value !== undefined) ? row.value : '—'}</span>,
      },
      {
        Header: 'Grupo',
        accessor: 'grupo_fisico_nome',
        width: 110,
        className: 'column_grid column_left',
        Cell: row => <span className="transito-cell-text transito-cell-text--left">{row.value || '—'}</span>,
      },
      {
        Header: 'Descrição',
        accessor: 'rotulo',
        minWidth: 120,
        className: 'column_grid column_left',
        Cell: row => <span className="transito-cell-text transito-cell-text--left">{row.value || '—'}</span>,
      },
      {
        Header: 'Status',
        id: 'status',
        width: 130,
        className: 'column_grid column_center',
        Cell: row => {
          const metros =
            (row.original.distancia_trecho_m !== null && row.original.distancia_trecho_m !== undefined) ? Math.round(row.original.distancia_trecho_m) : null;
          let badge;
          if (row.original.sem_dados) {
            badge = premiumBadge('Aguardando coleta', 'os-badge--gray');
          } else if (row.original.calibrando) {
            badge = premiumBadge('Calibrando', 'os-badge--blue');
          } else {
            badge = premiumBadge(
              row.original.congestionado ? 'Congestionado' : 'Fluido',
              row.original.congestionado ? 'os-badge--red' : 'os-badge--green',
            );
          }
          return (
            <div className="transito-status-cell">
              {badge}
              {(metros !== null && metros !== undefined) && <small className="transito-status-cell__trecho">{metros} m de trecho</small>}
            </div>
          );
        },
      },
      {
        Header: 'Tempo esp.',
        accessor: 'duracao_esperada_s',
        width: 96,
        className: 'column_grid column_center',
        Cell: row => (
          <span className="transito-cell-text">
            {formatDuracao(row.value)}
            {labelOrigemEsperado(row.original.esperado_origem, row.original.sem_dados)}
          </span>
        ),
      },
      {
        Header: 'Tempo real',
        accessor: 'duracao_real_s',
        width: 88,
        className: 'column_grid column_center',
        Cell: row => <span className="transito-cell-text">{formatDuracao(row.value)}</span>,
      },
      {
        Header: 'km/h esp.',
        accessor: 'velocidade_esperada_kmh',
        width: 80,
        className: 'column_grid column_center',
        Cell: row => <span className="transito-cell-text">{formatVelocidade(row.value)}</span>,
      },
      {
        Header: 'km/h real',
        accessor: 'velocidade_kmh',
        width: 80,
        className: 'column_grid column_center',
        Cell: row => <span className="transito-cell-text">{formatVelocidade(row.value)}</span>,
      },
      {
        Header: 'Atualizado',
        accessor: 'medido_em',
        width: 100,
        className: 'column_grid column_center',
        Cell: row => (
          <span className="transito-cell-text">{row.value ? moment(row.value).format('DD/MM HH:mm') : '—'}</span>
        ),
      },
    ];

    return (
      <PremiumTableShell summary={`${lista.length} fase(s) monitorada(s)`} hideToolbar tableMinWidth={0}>
        <ReactTableGT2A
          data={dados}
          columns={columns}
          className="-striped -highlight transito-premium-table"
          defaultPageSize={15}
          showPagination={dados.length > 15}
          filterable={false}
          showFilters={false}
          minRows={0}
          getTrProps={this.getTrProps}
          noDataText="Nenhuma fase monitorada."
        />
      </PremiumTableShell>
    );
  }

  renderDetalhe(fase, graficos) {
    const periodoLabel = (PERIODOS.find(p => p.value === this.state.periodo) || {}).label;

    return (
      <div className="transito-detalhe-section">
        <div className="transito-section-header">
          <div>
            <h5>
              <i className="fa fa-chart-line" /> Detalhe da fase
            </h5>
            <p>
              {formatarControlador(fase)}
              {(fase.anel !== null && fase.anel !== undefined) ? ` · Anel ${fase.anel}` : ''} · Fase {fase.fase_id}
 · período: {periodoLabel}
            </p>
          </div>
        </div>

        {this.renderKpisDetalhe()}

        {!graficos.temDados ? (
          <div className="os-graficos-empty transito-detalhe-empty">
            <i className="fa fa-chart-area" />
            <span>Ainda não há medições para este trecho no período selecionado.</span>
          </div>
        ) : (
          <div className="transito-chart-grid">
            <ChartCard titulo="Tempo de travessia — esperado vs real" subtitulo="Segundos no trecho monitorado" wide>
              <Line data={graficos.tempo} options={OPCOES_TEMPO} />
            </ChartCard>
            <ChartCard titulo="Velocidade — esperada vs real" subtitulo="km/h no trecho">
              <Line data={graficos.velocidade} options={OPCOES_VELOCIDADE} />
            </ChartCard>
            <ChartCard titulo="Índice de congestionamento" subtitulo="Real ÷ esperado">
              <Line data={graficos.ratio} options={OPCOES_RATIO} />
            </ChartCard>
          </div>
        )}

        {this.renderPerfilAprendido()}
      </div>
    );
  }

  renderPerfilAprendido() {
    const baseline = this.props.baseline || {};
    const horas = (baseline.horas || []).filter(h => h.util || h.fim_de_semana);

    const celula = bucket => {
      if (!bucket) return <span className="transito-perfil__vazio">—</span>;
      return (
        <span className="transito-perfil__valor">
          {formatDuracao(bucket.duracao_tipica_s)}
          <small>
            {formatVelocidade(bucket.velocidade_tipica_kmh)} · {bucket.amostras} amostra(s)
          </small>
        </span>
      );
    };

    return (
      <div className="transito-perfil">
        <div className="transito-section-header transito-perfil__header">
          <div>
            <h5>
              <i className="fa fa-brain" /> Perfil aprendido
            </h5>
            <p>
              Tempo típico do trecho por horário (mediana do histórico). É a régua usada para detectar
              congestionamento.
            </p>
          </div>
        </div>

        {!horas.length ? (
          <div className="os-graficos-empty transito-detalhe-empty">
            <i className="fa fa-hourglass-half" />
            <span>Ainda aprendendo o perfil deste trecho. Volte após algumas horas de coleta.</span>
          </div>
        ) : (
          <div className="transito-perfil__scroll">
            <table className="transito-perfil__table">
              <thead>
                <tr>
                  <th>Hora</th>
                  <th>Dia útil</th>
                  <th>Fim de semana</th>
                </tr>
              </thead>
              <tbody>
                {horas.map(h => (
                  <tr key={h.hora}>
                    <td className="transito-perfil__hora">{String(h.hora).padStart(2, '0')}h</td>
                    <td>{celula(h.util)}</td>
                    <td>{celula(h.fim_de_semana)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  renderVazio() {
    return (
      <div className="transito-empty-state">
        <div className="transito-empty-state__icon">
          <i className="fa fa-road" />
        </div>
        <h5>Nenhum trecho monitorado</h5>
      </div>
    );
  }

  render() {
    const resumo = this.props.resumo || [];
    const fases = this.props.fases || [];
    const totalMonitoradas = this.fasesDisponiveis().length;
    const fase = this.faseSelecionada();
    const graficos = this.dadosGraficos();
    const semFases = !fases.length && !resumo.length;

    const resumoLinha = semFases
      ? '0 fase(s) monitorada(s) — configure alertas de tráfego em uma fase'
      : `${totalMonitoradas} fase(s) monitorada(s) — atualização automática a cada 60 segundos`;

    const listaFiltrada = this.fasesBaseFiltradas();

    return (
      <div
        className="transito-premium ordem-servico-premium-list table-container"
        style={{ marginTop: 20, width: '100%' }}
      >
        <div className="os-list-top-panel">
          <p className="os-list-summary">{resumoLinha}</p>
          {this.renderFiltros(semFases)}
        </div>

        {semFases ? (
          this.renderVazio()
        ) : (
          <>
            <div className="transito-table-section">
              <div className="transito-section-header transito-section-header--compact">
                <div>
                  <h5>
                    <i className="fa fa-list" /> Visão geral
                  </h5>
                  <p>Clique em uma linha para ver histórico e gráficos da fase.</p>
                </div>
              </div>
              {this.renderTabela(listaFiltrada)}
            </div>

            {fase ? (
              this.renderDetalhe(fase, graficos)
            ) : (
              <div className="os-graficos-empty transito-select-hint">
                <i className="fa fa-hand-pointer" />
                <span>Selecione uma fase na tabela ou no filtro para visualizar o histórico.</span>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

function KpiCard({ label, valor, detalhe, cor, icone }) {
  return (
    <div className="os-graficos-kpi-card">
      <div className="os-graficos-kpi-icon" style={{ color: cor }}>
        <i className={`fa fa-${icone}`} />
      </div>
      <div className="os-graficos-kpi-body">
        <span className="os-graficos-kpi-label">{label}</span>
        <strong className="os-graficos-kpi-valor" style={{ color: cor }}>
          {valor}
        </strong>
        {detalhe && <small className="os-graficos-kpi-detalhe">{detalhe}</small>}
      </div>
    </div>
  );
}

function ChartCard({ titulo, subtitulo, children, wide }) {
  return (
    <div className={`os-graficos-chart-card${wide ? ' os-graficos-chart-card--wide' : ''}`}>
      <div className="os-graficos-chart-card-header">
        <h5>{titulo}</h5>
        {subtitulo && <p>{subtitulo}</p>}
      </div>
      <div className="os-graficos-chart-canvas">{children}</div>
    </div>
  );
}

const mapStateToProps = state => ({
  fases: state.transito.fases,
  resumo: state.transito.resumo,
  medicoes: state.transito.medicoes,
  baseline: state.transito.baseline,
  listGrupoFisico: state.grupoFisicoCad.list,
  listConfControladores: state.controladorCad.listConf,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    { init, getResumo, getMedicoes, limparMedicoes, getBaseline, limparBaseline, getGrupoFisicoList, getConfControladorGrupoList },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TransitoMonitor);
